import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home as HomeIcon, MapPin, Gem, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchSelect } from "@/components/SearchSelect";
import { VerifiedUnavailable } from "@/components/VerifiedUnavailable";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CoverageCTA } from "@/components/CoverageCTA";
import { ptMeta, PROPERTY_TYPES, getPreferredType, setPreferredType } from "@/lib/propertyTypes";
import { getRecent } from "@/lib/recentListings";
import RecentlyViewed from "@/components/RecentlyViewed";
import ScoreBadge, { UnverifiedBadge } from "@/components/ScoreBadge";
import { FreshnessNote } from "@/components/FreshnessNote";
import ZipHeatStrip from "@/components/HeatStrip";
import { copyText } from "@/lib/clipboard";
import { groupStatesByRegion } from "@/lib/geo";

export default function Properties() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [geoStates, setGeoStates] = useState([]);
  const [geoCities, setGeoCities] = useState([]);
  const [geoZips, setGeoZips] = useState([]);
  const [zipHeat, setZipHeat] = useState(null);
  const [zipHeatLoading, setZipHeatLoading] = useState(false);
  const [filters, setFilters] = useState({ city: "", state: "", zip: "", property_type: getPreferredType(), price_min: "1000000", price_max: "", beds: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [baseParams, setBaseParams] = useState(null);
  const [sort, setSort] = useState("price_desc");
  const PAGE = 24;
  const recentHomes = getRecent("home");
  const recentIds = new Set(recentHomes.map((r) => r.id));
  const firstRecentId = recentHomes[0]?.id;
  const recentRef = useRef(null);
  const [scores, setScores] = useState({});
  const [scoresLoaded, setScoresLoaded] = useState(false);
  const [bestValue, setBestValue] = useState(false);
  const [premium, setPremium] = useState(false);

  useEffect(() => { api.get("/config/geo").then((r) => setGeoStates(r.data.options || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/billing/status").then((r) => setPremium(!!r.data.premium)).catch(() => {}); }, []);

  useEffect(() => {
    if (!filters.state) { setZipHeat(null); return; }
    setZipHeatLoading(true);
    api.get("/config/zip-medians", { params: { state: filters.state, ...(filters.city ? { city: filters.city } : {}) } })
      .then((r) => setZipHeat(r.data))
      .catch(() => setZipHeat(null))
      .finally(() => setZipHeatLoading(false));
  }, [filters.state, filters.city]);

  const selectZipFromHeat = (zip) => {
    const nf = { ...filters, zip };
    setFilters(nf);
    if (filters.state) api.get("/config/geo", { params: { state: filters.state, city: filters.city } }).then((r) => setGeoZips(r.data.options || [])).catch(() => {});
    runSearch(Object.fromEntries(Object.entries(nf).filter(([, v]) => v !== "")));
  };

  useEffect(() => {
    if (recentRef.current) recentRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [data]);

  useEffect(() => {
    if (data?.available && data.listings?.length) {
      setScoresLoaded(false);
      api.post("/valuations/batch", { kind: "home", listings: data.listings })
        .then((r) => setScores(r.data.results || {}))
        .catch(() => setScores({}))
        .finally(() => setScoresLoaded(true));
    } else {
      setScores({});
      setScoresLoaded(false);
    }
  }, [data]);

  const [freshDays, setFreshDays] = useState(0);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const selectState = (st) => {
    setFilters((f) => ({ ...f, state: st, city: "", zip: "" }));
    setGeoCities([]); setGeoZips([]);
    if (st) api.get("/config/geo", { params: { state: st } }).then((r) => setGeoCities(r.data.options || [])).catch(() => {});
  };
  const selectCity = (city) => {
    setFilters((f) => ({ ...f, city, zip: "" }));
    setGeoZips([]);
    if (city && filters.state) api.get("/config/geo", { params: { state: filters.state, city } }).then((r) => setGeoZips(r.data.options || [])).catch(() => {});
  };

  const runSearch = async (params, sortVal = sort, bv = bestValue) => {
    setBaseParams(params);
    setLoading(true);
    try {
      const res = await api.get("/properties", { params: { ...params, sort: sortVal, best_value: bv, limit: PAGE, offset: 0 }, timeout: 45000 });
      setData(res.data);
    } catch { setData({ available: false, listings: [], total: 0 }); toast.error(bv ? "Best-value ranking took too long. Please try again or narrow your search." : "Search took too long. Please try again."); }
    finally { setLoading(false); }
  };

  const search = () => runSearch(Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "")));

  const filterByType = (t) => {
    setPreferredType(t);
    const nf = { ...filters, property_type: t };
    setFilters(nf);
    runSearch(Object.fromEntries(Object.entries(nf).filter(([, v]) => v !== "")));
  };

  const setPropertyType = (t) => { setPreferredType(t); set("property_type", t); };

  const changeSort = (v) => { setSort(v); if (baseParams) runSearch(baseParams, v, bestValue); };
  const toggleBestValue = () => { const nb = !bestValue; setBestValue(nb); if (baseParams) runSearch(baseParams, sort, nb); };

  const loadMore = async () => {
    if (!baseParams) return;
    setLoadingMore(true);
    try {
      const res = await api.get("/properties", { params: { ...baseParams, sort, best_value: bestValue, limit: PAGE, offset: data.listings.length }, timeout: 45000 });
      setData((d) => ({ ...res.data, listings: [...d.listings, ...res.data.listings] }));
    } catch {} finally { setLoadingMore(false); }
  };

  const viewAll = () => {
    setFilters({ city: "", state: "", zip: "", property_type: "", price_min: "1000000", price_max: "", beds: "" });
    setGeoCities([]); setGeoZips([]);
    runSearch({});
  };

  const FRESH_DAY_MS = 86400000;
  const shownListings = (data?.listings || []).filter((l) => !freshDays || (l._last_seen && (Date.now() - new Date(l._last_seen).getTime()) / FRESH_DAY_MS <= freshDays));

  return (
    <div className="max-w-[1600px] mx-auto px-5 sm:px-8 py-12">
      <div className="flex items-center gap-3 mb-2">
        <HomeIcon className="w-6 h-6" strokeWidth={1.5} />
        <h1 className="font-display text-4xl tracking-tighter font-medium">Luxury Homes</h1>
      </div>
      <p className="text-muted-foreground mb-8">Verified for-sale listings via RentCast — real MLS numbers, agents & price history.</p>

      <div className="bg-surface border border-border rounded-xl p-6 grid grid-cols-2 md:grid-cols-6 gap-4 mb-10">
        <div className="col-span-2 md:col-span-2">
          <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">State</Label>
          <SearchSelect
            testid="filter-state"
            value={filters.state}
            onChange={selectState}
            placeholder="Select a state"
            searchPlaceholder="Search state…"
            emptyText="No state."
            className="mt-1 w-full"
            groups={groupStatesByRegion(geoStates).map((g) => ({ label: g.region, options: g.options.map((o) => ({ value: o.value, count: o.count })) }))}
          />
        </div>
        <div className="col-span-2 md:col-span-2">
          <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">City</Label>
          <SearchSelect
            testid="filter-city"
            value={filters.city}
            onChange={selectCity}
            disabled={!filters.state || !geoCities.length}
            placeholder={filters.state ? "Select a city" : "Select a state first"}
            searchPlaceholder="Search city…"
            emptyText="No city."
            className="mt-1 w-full"
            options={geoCities.map((o) => ({ value: o.value, count: o.count }))}
          />
        </div>
        <div className="col-span-2 md:col-span-2">
          <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">ZIP Code</Label>
          <SearchSelect
            testid="filter-zip"
            value={filters.zip}
            onChange={(v) => set("zip", v)}
            disabled={!filters.city || !geoZips.length}
            placeholder={filters.city ? "Any ZIP" : "Select a city first"}
            searchPlaceholder="Search ZIP…"
            emptyText="No ZIP."
            allOption={{ value: "", label: "Any ZIP" }}
            className="mt-1 w-full"
            options={geoZips.map((o) => ({ value: o.value, count: o.count }))}
          />
        </div>
        <div className="col-span-2 md:col-span-2">
          <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">Type</Label>
          <SearchSelect
            testid="filter-property-type"
            value={filters.property_type}
            onChange={(v) => setPropertyType(v)}
            allOption={{ value: "", label: "Any" }}
            placeholder="Any"
            searchPlaceholder="Search type…"
            className="mt-1 w-full"
            options={PROPERTY_TYPES.map((t) => ({ value: t }))}
          />
        </div>
        <div className="col-span-2 md:col-span-2">
          <Label className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground">Min $</Label>
          <Input data-testid="filter-price-min" type="number" min="1000000" step="100000" value={filters.price_min}
            onChange={(e) => set("price_min", e.target.value)}
            onBlur={(e) => { if (!e.target.value || Number(e.target.value) < 1000000) set("price_min", "1000000"); }}
            className="mt-1" />
        </div>
        <div className="col-span-2 md:col-span-6 flex flex-wrap gap-3">
          <Button onClick={search} data-testid="search-properties-button" className="rounded-full px-8">Search</Button>
          <Button onClick={viewAll} variant="outline" data-testid="view-all-homes-button" className="rounded-full"><HomeIcon className="w-4 h-4 mr-2" />View All Homes</Button>
        </div>
      </div>

      <ZipHeatStrip
        tiles={(zipHeat?.zips || []).map((z) => ({ key: z.zip, label: z.zip, median: z.median, count: z.count }))}
        min={zipHeat?.min}
        max={zipHeat?.max}
        loading={zipHeatLoading}
        activeKey={filters.zip}
        onSelect={selectZipFromHeat}
        unit="listing"
        testidPrefix="zip-heat"
        title={`ZIP median prices${filters.city ? ` · ${filters.city}, ${filters.state}` : filters.state ? ` · ${filters.state}` : ""}`}
      />

      <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="home-type-filter">
        <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mr-1">Type</span>
        <button
          type="button"
          onClick={() => filterByType("")}
          data-testid="home-type-all"
          className={`font-mono-data text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${!filters.property_type ? "border-accent text-accent bg-accent/10" : "border-border text-muted-foreground hover:text-foreground"}`}
        >
          All
        </button>
        {PROPERTY_TYPES.map((t) => {
          const m = ptMeta(t);
          const active = filters.property_type === t;
          return (
            <button
              key={t}
              type="button"
              onClick={() => filterByType(active ? "" : t)}
              data-testid={`home-type-${t}`}
              className={`font-mono-data text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border flex items-center gap-1.5 transition-colors ${active ? m.cls : "border-border text-muted-foreground hover:text-foreground"}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
              {t}
            </button>
          );
        })}
      </div>

      <RecentlyViewed kind="home" items={recentHomes} onSelect={(item) => navigate(`/homes/${encodeURIComponent(item.id)}`)} />

      {data && !loading && data.available && (data.listings.length > 0 || bestValue) && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          {premium ? (
            <button
              type="button"
              onClick={toggleBestValue}
              data-testid="best-value-filter"
              className={`font-mono-data text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full border flex items-center gap-1.5 transition-colors ${bestValue ? "border-emerald-500/50 text-emerald-400 bg-emerald-500/10" : "border-border text-muted-foreground hover:text-foreground"}`}
            >
              <Gem className="w-3.5 h-3.5" strokeWidth={1.5} />Best value{bestValue ? ` · ${data.total}` : ""}
            </button>
          ) : <span />}
          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mr-2">Fresh</span>
              <SearchSelect
                testid="fresh-filter-homes"
                value={String(freshDays)}
                onChange={(v) => setFreshDays(Number(v))}
                align="end"
                options={[
                  { value: "0", label: "All listings" },
                  { value: "3", label: "Verified ≤ 3 days" },
                  { value: "7", label: "Verified ≤ 7 days" },
                  { value: "14", label: "Verified ≤ 14 days" },
                  { value: "30", label: "Verified ≤ 30 days" },
                ]}
              />
            </div>
            <div className="flex items-center">
              <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-foreground mr-2">Sort</span>
              <SearchSelect
                testid="sort-homes"
                value={sort}
                onChange={changeSort}
                align="end"
                options={[
                  { value: "price_desc", label: "Price: High → Low" },
                  { value: "price_asc", label: "Price: Low → High" },
                  { value: "newest", label: "Newest" },
                  ...(premium ? [{ value: "score_desc", label: "Investment Score" }] : []),
                ]}
              />
            </div>
          </div>
        </div>
      )}

      {loading && <div className="font-mono-data text-sm text-muted-foreground animate-pulse">Querying verified sources…</div>}

      {data && !loading && (
        !data.available ? (
          <VerifiedUnavailable note="No verified listings matched your search. Try a broader area or different filters." />
        ) : data.listings.length === 0 ? (
          bestValue
            ? <VerifiedUnavailable testId="no-best-value" note="No listings in this search have a verified Strong-Value score. Turn off ‘Best value’ to see all listings." />
            : <VerifiedUnavailable note="No verified listings matched your search. Try a broader area or different filters." />
        ) : shownListings.length === 0 ? (
          <VerifiedUnavailable testId="no-fresh-homes" note={`No listings on this page were verified within the last ${freshDays} days. Widen the freshness filter to see more.`} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {shownListings.map((l, i) => (
              <motion.div key={l.id || i} ref={l.id === firstRecentId ? recentRef : undefined} whileHover={{ y: -6 }} onClick={() => navigate(`/homes/${encodeURIComponent(l.id)}`)}
                className={`bg-surface border rounded-xl p-6 cursor-pointer transition-all hover:shadow-lg ${recentIds.has(l.id) ? "border-accent ring-2 ring-accent/40" : "border-border hover:border-accent/40"}`} data-testid="property-card">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-mono-data text-2xl">{l.price ? `$${Number(l.price).toLocaleString()}` : "—"}</div>
                  <div className="flex items-center gap-2 shrink-0">
                    {scores[l.id] ? <ScoreBadge data={scores[l.id]} /> : scoresLoaded ? <UnverifiedBadge /> : null}
                    {l.property_type && (
                      <span className={`font-mono-data text-[10px] uppercase tracking-wider border rounded-full px-2.5 py-1 flex items-center gap-1.5 ${ptMeta(l.property_type).cls}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${ptMeta(l.property_type).dot}`} />{l.property_type}
                      </span>
                    )}
                  </div>
                </div>
                <div className="font-editorial text-xl italic mt-3 line-clamp-1">{l.address || "Verified data unavailable"}</div>
                <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1"><MapPin className="w-3.5 h-3.5" />{[l.city, l.state].filter(Boolean).join(", ") || "—"}</div>
                <FreshnessNote lastSeen={l._last_seen} agingOnly className="mt-2" />
                <div className="grid grid-cols-4 gap-2 mt-5 pt-4 border-t border-border">
                  {[["BD", l.beds], ["BA", l.baths], ["SQFT", l.sqft ? Number(l.sqft).toLocaleString() : null], ["$/FT", l.price_per_sqft ? `$${Math.round(l.price_per_sqft)}` : null]].map(([lab, val]) => (
                    <div key={lab}>
                      <div className="font-mono-data text-sm">{val ?? "—"}</div>
                      <div className="font-mono-data text-[9px] uppercase tracking-wider text-muted-foreground mt-0.5">{lab}</div>
                    </div>
                  ))}
                </div>
                {l.mls_number && (
                  <div className="flex items-center justify-between gap-2 mt-4">
                    <div className="font-mono-data text-[10px] text-muted-foreground/70 uppercase tracking-wider">MLS {l.mls_number}{l.days_on_market != null ? ` · ${l.days_on_market}d on market` : ""}</div>
                    <button
                      data-testid="card-copy-mls"
                      title="Copy MLS number"
                      onClick={async (e) => { e.stopPropagation(); (await copyText(l.mls_number)) ? toast.success("MLS number copied") : toast.error("Could not copy"); }}
                      className="shrink-0 text-muted-foreground hover:text-accent transition-colors"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )
      )}
      {data && !loading && data.available && data.listings.length > 0 && (
        <div className="flex flex-col items-center gap-3 mt-10">
          <div className="font-mono-data text-[11px] uppercase tracking-wider text-muted-foreground" data-testid="homes-count">Showing {shownListings.length} of {data.total} verified listings</div>
          {data.listings.length < data.total && (
            <Button onClick={loadMore} disabled={loadingMore} variant="outline" data-testid="load-more-homes-button" className="rounded-full px-8">{loadingMore ? "Loading…" : "Load more"}</Button>
          )}
        </div>
      )}
      {data && !loading && data.available && data.listings.length > 0 && <CoverageCTA kind="homes" />}
      {!data && !loading && (
        <VerifiedUnavailable testId="properties-initial-empty" note="Search a city (e.g., Beverly Hills, CA) to view verified for-sale listings." />
      )}
    </div>
  );
}
